import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { pull, filter } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { ModelYear, PurchaseMethods, Vehicle } from '@sm360/widget-commons-vehicle';
import { LoaderAnim } from '@sm360/widget-commons-basic-components';
import { AnalyticsService, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';

import ShowroomHeader from 'build-and-quote/components/ShowroomHeader/ShowroomHeader.jsx';
import ModelYears from 'build-and-quote/components/ModelYears/ModelYears.jsx';
import ModelTrims from 'build-and-quote/components/ModelTrims/ModelTrims.jsx';
import DrivePowerTrains from 'build-and-quote/components/DrivePowerTrains/DrivePowerTrains.jsx';
import OptionsPackages from 'build-and-quote/components/OptionsPackages/OptionsPackages.jsx';
import Options from 'build-and-quote/containers/Options/Options.js';
import Summary from 'build-and-quote/components/Summary/Summary.jsx';
import SummaryV25 from 'build-and-quote/components/Summary/SummaryV25.jsx';
import StepsSection from 'build-and-quote/components/Buttons/StepsSection.jsx';
import OptionsResolutionsRender from 'build-and-quote/containers/OptionsResolutions/OptionsResolutions.js';

import * as Actions from 'build-and-quote/actions/buildAndQuoteActions.js';
import BuildAndQuoteModel from 'build-and-quote/models/BuildAndQuoteModel.js';
import { MODEL_TRIMS, MODEL_YEARS, CONFIRMATION } from 'build-and-quote/constants/sections.js';

import 'build-and-quote/components/BuildAndQuote/buildAndQuote.scss';

class BuildAndQuote extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            onlyOnce: true,
        };
        this.handleMobilePaymentCalculatorOpened = this.handleMobilePaymentCalculatorOpened.bind(this);
        this.handleModelYearSelected = this.handleModelYearSelected.bind(this);
        this.handleTrimSelected = this.handleTrimSelected.bind(this);
        this.handleExtColorSelected = this.handleExtColorSelected.bind(this);
        this.handleIntColorSelected = this.handleIntColorSelected.bind(this);
        this.handleEngineSelected = this.handleEngineSelected.bind(this);
        this.handleOptionSelected = this.handleOptionSelected.bind(this);
        this.scrollTop = this.scrollTop.bind(this);
        this.trackPreviousStepButton = this.trackPreviousStepButton.bind(this);
    }

    scrollTop() {
        const windowGet = WindowProxy.get();
        if (windowGet) {
            const nav = windowGet.document.getElementById('build-and-price__nav');
            if (nav && nav.offsetTop) {
                windowGet.scroll({
                    top: nav.offsetTop,
                    behavior: 'smooth',
                });
            }
        }
    }

    handleMobilePaymentCalculatorOpened() {
        this.props.actions.setMobilePaymentCalculatorOpened(true);
    }

    handleModelYearSelected(year) {
        const vehicleIsAlreadyConfigured = !!this.props.vehicle.configurationToken;
        if (vehicleIsAlreadyConfigured) {
            return;
        }
        AnalyticsService.ga4Trigger(TAGS.YEAR_SELECTION(this.props.modelYear.model, year.year));
        this.props.actions.selectModelYear(year);
        this.props.actions.changeBuildAndQuoteSection(MODEL_TRIMS.value);
        this.scrollTop();
    }

    handleTrimSelected(trim) {
        this.props.actions.selectModelTrim(trim);
        AnalyticsService.ga4Trigger(
            TAGS.TRIM_SELECTION(trim.label, this.props.modelYear.model, this.props.modelYear.year)
        );
    }

    handleExtColorSelected(extColour) {
        this.props.actions.selectExteriorColour(extColour);
        AnalyticsService.ga4Trigger(
            TAGS.EXTERIOR_COLOUR(extColour.colourDescription, this.props.modelYear.model, this.props.modelYear.year)
        );
    }

    handleIntColorSelected(intColour) {
        this.props.actions.selectInteriorColour(intColour);
        AnalyticsService.ga4Trigger(
            TAGS.INTERIOR_COLOUR(intColour.colourDescription, this.props.modelYear.model, this.props.modelYear.year)
        );
    }

    handleEngineSelected(engine) {
        this.props.actions.selectDrivePowerTrain(engine);
        AnalyticsService.ga4Trigger(
            TAGS.ENGINE(engine.selectionCode, this.props.modelYear.model, this.props.modelYear.year)
        );
    }

    handleOptionSelected(option) {
        this.props.actions.selectOptionsPackage(option);
        AnalyticsService.ga4Trigger(
            TAGS.COMFORT(
                option.configuration.package.description || LocaleService.t('optionsPackageSection.standard'),
                this.props.modelYear.model,
                this.props.modelYear.year
            )
        );
    }

    trackPreviousStepButton() {
        AnalyticsService.gaTrigger(TAGS.PREVIOUS_STEP_BTN(WindowProxy.location().pathname, this.props.modelYear.model));
    }

    render() {
        const { purchaseMethods, modelYear, buildAndQuote, actions } = this.props;
        let taxesIncluded = false;
        let taxCodes = [];

        const availableModelYears = modelYear.years;
        const hasOnlyOnePackage = modelYear.isSingleConfiguration;
        const { hasOptions } = modelYear;
        const availableOptionsPackagesList = modelYear.optionsPackages.filter(
            (optionsPackage) => optionsPackage.visible
        );

        const { imageServer, mobilePaymentCalculatorOpened } = buildAndQuote;

        const vehicleTitleMake = modelYear.modelContainsMake() ? '' : modelYear.make;

        const selectedModel = {
            bodyStyleCode: modelYear.bodyStyleCode,
            isSingleConfiguration: modelYear.isSingleConfiguration,
            hasOptions: modelYear.hasOptions,
            year: modelYear.year,
            make: vehicleTitleMake,
            model: modelYear.model,
        };
        const selectedModelTrim = modelYear.getSelectedModelTrim();
        const selectedOptionsPackage = availableOptionsPackagesList
            .filter((optionsPackage) => optionsPackage.selected)
            .first();

        const modelTrim = selectedModelTrim ? selectedModelTrim.getDescription(modelYear.model) : '';
        const modelTrimExtension = selectedModelTrim ? selectedModelTrim.trimExtensionDescription : '';

        const optionsPackageDescription = selectedOptionsPackage ? selectedOptionsPackage.description : '';

        if (purchaseMethods) {
            taxesIncluded = purchaseMethods.taxesIncluded;
            taxCodes = purchaseMethods.taxCodes;
        }

        const allFinancesPaymentFrequencyLabels = this.props.purchaseMethods.getPaymentFrequencyLabels(
            true,
            LocaleService
        );

        const currentSection = buildAndQuote.buildAndQuoteSection;

        const buildAndPriceClasses = ['showroom-build-and-price'];

        const hasOptionPackage = selectedOptionsPackage
            ? !!filter(selectedOptionsPackage.configuration.options, 'visible').length
            : false;

        if (mobilePaymentCalculatorOpened) {
            buildAndPriceClasses.push('mobileCalculatorOpen');
        } else {
            pull(buildAndPriceClasses, 'mobileCalculatorOpen');
        }

        const optionsBlock = !hasOnlyOnePackage && (
            <OptionsPackages
                currentSection={currentSection}
                availableOptionsPackages={availableOptionsPackagesList.toArray()}
                selectedModel={selectedModel}
                selectedOptionsPackage={selectedOptionsPackage}
                selectOptionsPackage={this.handleOptionSelected}
                hasAdobeTagging={this.props.hasAdobeTagging}
            />
        );

        const optionsAccessoriesBlock = hasOptions &&
            hasOptionPackage && (
                <Options
                    configurationToken={this.props.vehicle.configurationToken}
                    currentSection={currentSection}
                    selectedModel={selectedModel}
                    selectedOptionsPackage={selectedOptionsPackage}
                    hasAdobeTagging={this.props.hasAdobeTagging}
                />
            );

        const configuration = modelYear.getSelectedOptionsPackageConfiguration();
        const selectedAccessories = configuration ? configuration.getPurchaseMethodsSelectedOptions() : [];

        // Prevent build-and-quote to display `no-photo` picture, when vehicle is not loaded
        if (!this.props?.vehicle?.vehicleId && this.props.modelYear.year === 0) {
            return (
                <div className="showroom-build-and-price-loader">
                    <LoaderAnim />
                </div>
            );
        }

        // this will scroll to top when the user reaches the last step.
        if (currentSection === 6 && this.state.onlyOnce) {
            this.setState({ onlyOnce: false });
            this.scrollTop();
        }

        const isDisplayStepSection = currentSection !== MODEL_YEARS.value && currentSection !== CONFIRMATION.value;

        const stepSection = isDisplayStepSection && (
            <StepsSection
                isSingleConfiguration={hasOnlyOnePackage}
                hasOptions={hasOptions && hasOptionPackage}
                currentSection={currentSection}
                configurationToken={this.props.vehicle.configurationToken}
                onPreviousStep={this.trackPreviousStepButton}
            />
        );

        const isVdp = this.props.vehicle?.vehicleId;
        const isSsr = !WindowProxy.get();

        return (
            <div className={buildAndPriceClasses.join(' ')}>
                <div
                    className="showroom-build-and-price__content"
                    ref={(s) => {
                        this.buildAndPriceContent = s;
                    }}
                >
                    <div className="showroom-build-and-price__inner-content" id="build-and-price__nav">
                        {!isSsr && (
                            <ShowroomHeader
                                selectedModel={selectedModel}
                                modelTrim={modelTrim}
                                modelTrimExtension={modelTrimExtension}
                                optionsPackage={optionsPackageDescription}
                                imageServer={imageServer}
                                modelYearColor={modelYear.getSelectedExteriorColour()}
                                currentSection={currentSection}
                                hasOptionPackage={hasOptionPackage}
                                vehicle={this.props.vehicle}
                                hasAdobeTagging={this.props.hasAdobeTagging}
                                displayCreditRate={this.props.displayCreditRate}
                                displayFactoryOrder={this.props.displayFactoryOrder}
                            />
                        )}
                        {selectedModel.year > 0 && (
                            <ModelYears
                                currentSection={currentSection}
                                paymentFrequencyLabels={allFinancesPaymentFrequencyLabels}
                                availableModelYears={availableModelYears.toArray()}
                                selectedModel={selectedModel}
                                imageServer={imageServer}
                                selectModelYear={this.handleModelYearSelected}
                                displayCreditRate={this.props.displayCreditRate}
                                displayFactoryOrder={this.props.displayFactoryOrder}
                            />
                        )}

                        <ModelTrims
                            currentSection={currentSection}
                            modelYear={modelYear}
                            imageServer={imageServer}
                            selectedModel={selectedModel}
                            selectCabSize={actions.selectCabSize}
                            selectBoxSize={actions.selectBoxSize}
                            selectModelTrim={this.handleTrimSelected}
                            selectExteriorColour={this.handleExtColorSelected}
                            selectInteriorColour={this.handleIntColorSelected}
                            hasAdobeTagging={this.props.hasAdobeTagging}
                        />

                        <DrivePowerTrains
                            currentSection={currentSection}
                            modelYear={modelYear}
                            selectDrivePowerTrain={this.handleEngineSelected}
                            selectedModel={selectedModel}
                            hasAdobeTagging={this.props.hasAdobeTagging}
                        />

                        {optionsBlock}

                        {optionsAccessoriesBlock}
                        {isVdp ? (
                            <SummaryV25
                                vehicle={this.props.vehicle}
                                isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                                saveToGarage={actions.saveToGarageV25}
                                imageServer={imageServer}
                                websiteSoldDaysShown={this.props.websiteSoldDaysShown}
                                organizationId={this.props.organizationId}
                                config={this.props.config}
                                hasAdobeTagging={this.props.hasAdobeTagging}
                                displayCreditRate={this.props.displayCreditRate}
                            />
                        ) : (
                            <Summary
                                selectedModel={selectedModel}
                                selectedModelTrim={selectedModelTrim}
                                selectedOptionsPackageExteriorColour={modelYear.getSelectedOptionsPackageExteriorColour()}
                                selectedOptionsPackageInteriorColour={modelYear.getSelectedOptionsPackageInteriorColour()}
                                selectedDrivePowerTrain={modelYear.getSelectedDrivePowerTrain()}
                                selectedOptionsPackage={modelYear.getSelectedOptionsPackage()}
                                selectedCabSize={modelYear.getSelectedCabSize()}
                                selectedBoxSize={modelYear.getSelectedBoxSize()}
                                selectedAccessories={selectedAccessories}
                                taxesIncluded={taxesIncluded}
                                taxCodes={taxCodes}
                                imageServer={imageServer}
                                currentSection={currentSection}
                                saveToGarage={actions.saveToGarage}
                                isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                                hasAdobeTagging={this.props.hasAdobeTagging}
                                displayCreditRate={this.props.displayCreditRate}
                                displayFactoryOrder={this.props.displayFactoryOrder}
                                config={this.props?.config}
                            />
                        )}
                        <OptionsResolutionsRender />

                        {stepSection}
                    </div>
                </div>
            </div>
        );
    }
}

BuildAndQuote.defaultProps = {
    vehicle: PropTypes.shape({
        configurationToken: '',
    }),
    websiteSoldDaysShown: 0,
    config: {},
    hasAdobeTagging: false,
    displayCreditRate: false,
    displayFactoryOrder: false,
};

BuildAndQuote.propTypes = {
    actions: PropTypes.shape({
        setQuote: PropTypes.func.isRequired,
        selectCabSize: PropTypes.func.isRequired,
        selectBoxSize: PropTypes.func.isRequired,
        selectModelYear: PropTypes.func.isRequired,
        selectModelTrim: PropTypes.func.isRequired,
        selectExteriorColour: PropTypes.func.isRequired,
        selectInteriorColour: PropTypes.func.isRequired,
        selectDrivePowerTrain: PropTypes.func.isRequired,
        selectOptionsPackage: PropTypes.func.isRequired,
        setMobilePaymentCalculatorOpened: PropTypes.func.isRequired,
        saveToGarage: PropTypes.func.isRequired,
        changeBuildAndQuoteSection: PropTypes.func.isRequired,
    }).isRequired,
    buildAndQuote: PropTypes.instanceOf(BuildAndQuoteModel).isRequired,
    modelYear: PropTypes.instanceOf(ModelYear.ModelYearModel).isRequired,
    purchaseMethods: PropTypes.instanceOf(PurchaseMethods.PurchaseMethodsModel).isRequired,
    vehicle: PropTypes.oneOfType([
        PropTypes.instanceOf(Vehicle.VehicleModel),
        PropTypes.instanceOf(Vehicle.VehicleModelV25),
    ]),
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    websiteSoldDaysShown: PropTypes.number,
    config: PropTypes.object,
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
    displayFactoryOrder: PropTypes.bool,
};

BuildAndQuote.displayName = 'BuildAndQuote';

function mapStateToProps(state) {
    return {
        buildAndQuote: state.buildAndQuote,
        modelYear: state.modelYear,
        purchaseMethods: state.purchaseMethods,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
    };
}

export { BuildAndQuote };
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(BuildAndQuote);
